import React, { useState, useEffect, useCallback, Fragment } from "react";
import {
  Col,
  Card,
  CardBody,
  CardHeader,
  Input,
  Label,
  Button,
  Spinner,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  UncontrolledTooltip,
  Collapse,
} from "reactstrap";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const getAuthHeaders = () => {
  const authUser = JSON.parse(sessionStorage.getItem("authUser"));
  return {
    "Content-Type": "application/json",
    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
  };
};

const handleAuthError = () => {
  sessionStorage.removeItem("authUser");
  window.location.href = "/login";
};

// ─── Severity helpers ────────────────────────────────────────────────────────

// ─── Shared severity colour palette (matches EnhancedSeverityTiles exactly) ──
const SEVERITY_COLORS = {
  Critical: "#dc2626",
  High:     "#ea580c",
  Medium:   "#d97706",
  Low:      "#16a34a",
  default:  "#6c757d",
};

const pageIconChip = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: "1px solid rgba(255, 107, 53, 0.55)",
  background: "rgba(255, 107, 53, 0.10)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxSizing: "border-box",
};

const severityColor = (severity) =>
  SEVERITY_COLORS[severity] || SEVERITY_COLORS.default;

const cvssColor = (score) => {
  const n = parseFloat(score);
  if (isNaN(n))  return SEVERITY_COLORS.default;
  if (n >= 9.0)  return SEVERITY_COLORS.Critical;
  if (n >= 7.0)  return SEVERITY_COLORS.High;
  if (n >= 4.0)  return SEVERITY_COLORS.Medium;
  return SEVERITY_COLORS.Low;
};

const SEV_BG = { Critical: "#fef2f2", High: "#fff7ed", Medium: "#fffbeb", Low: "#f0fdf4" };

const getSeverityStyle = (severity) => ({
  backgroundColor: SEV_BG[severity] || "transparent",
  color: severityColor(severity),
  border: `1px solid ${severityColor(severity)}`,
  padding: "4px 12px",
  borderRadius: "4px",
  fontWeight: "600",
});

const getSeverityIcon = (severity) => {
  const icons = {
    Critical: "ri-error-warning-fill",
    High:     "ri-alert-fill",
    Medium:   "ri-information-fill",
    Low:      "ri-checkbox-circle-fill",
  };
  return icons[severity] || "ri-checkbox-blank-circle-line";
};

const getCVSSStyle = (score) => {
  const color = cvssColor(score);
  return {
    backgroundColor: "transparent",
    color,
    border: `1px solid ${color}`,
    padding: "4px 8px",
    borderRadius: "4px",
    fontWeight: "600",
  };
};

const getRiskLevel = (critical, high) => {
  const total = parseInt(critical || 0) + parseInt(high || 0);
  if (total >= 10) return { label: "Critical", color: SEVERITY_COLORS.Critical };
  if (total >= 5)  return { label: "High",     color: SEVERITY_COLORS.High     };
  if (total > 0)   return { label: "Medium",   color: SEVERITY_COLORS.Medium   };
  return                  { label: "Secure",   color: SEVERITY_COLORS.Low      };
};

// Reusable transparent badge style builders
const transparentBadge = (color) => ({
  backgroundColor: "transparent",
  color,
  border: `1px solid ${color}`,
  padding: "4px 12px",
  borderRadius: "20px",
  fontWeight: "600",
  display: "inline-block",
  fontSize: "12px",
});

const transparentTag = (color) => ({
  backgroundColor: "transparent",
  color,
  border: `1px solid ${color}`,
  padding: "2px 8px",
  borderRadius: "4px",
  fontWeight: "600",
  display: "inline-block",
  fontSize: "11px",
});

const getOSIcon = (os) => {
  if (!os) return "ri-computer-line";
  const l = os.toLowerCase();
  if (l.includes("windows")) return "ri-windows-line";
  if (l.includes("linux") || l.includes("ubuntu")) return "ri-ubuntu-line";
  if (l.includes("mac") || l.includes("darwin")) return "ri-apple-line";
  return "ri-computer-line";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const diffDays = Math.floor(Math.abs(new Date() - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)  return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const formatCVSS = (score) => {
  const n = parseFloat(score);
  return isNaN(n) ? null : n.toFixed(1);
};

const parseReferenceUrls = (urls) => {
  if (!urls) return [];
  if (Array.isArray(urls)) return urls;
  try { return JSON.parse(urls); } catch { return []; }
};

// ─── Component ───────────────────────────────────────────────────────────────

const EnhancedVulnerabilityTable = () => {
  const [loading, setLoading]                     = useState(true);
  const [agents, setAgents]                       = useState([]);
  const [pagination, setPagination]               = useState({ total: 0, pages: 0, page: 1 });
  const [selectedAgent, setSelectedAgent]         = useState(null);
  const [agentVulnerabilities, setAgentVulnerabilities] = useState([]);
  const [agentStats, setAgentStats]               = useState(null);
  const [modalOpen, setModalOpen]                 = useState(false);
  const [modalLoading, setModalLoading]           = useState(false);
  const [showFilters, setShowFilters]             = useState(false);
  const [error, setError]                         = useState(null);
  const [modalError, setModalError]               = useState(null);
  const [expandedVuln, setExpandedVuln]           = useState(null);

  const [filters, setFilters] = useState({
    agent_name: "",
    os: "",
    min_vulns: "",
    page: 1,
    per_page: 20,
  });

  // ── Fetch agents list ──────────────────────────────────────────────────────
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.agent_name) params.append("agent_name", filters.agent_name);
      if (filters.os)         params.append("os", filters.os);
      if (filters.min_vulns)  params.append("min_vulns", filters.min_vulns);
      params.append("page",     filters.page);
      params.append("per_page", filters.per_page);

      const response = await fetch(
        `${API_BASE_URL}/vulnerabilities/agents/list?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      if (response.status === 401) { handleAuthError(); return; }

      const data = await response.json();
      if (data.success) {
        setAgents(data.agents || []);
        setPagination({
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0,
          page:  data.pagination?.page  || 1,
        });
      } else {
        setError(data.error || "Failed to fetch agents");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // ── Fetch per-agent vulnerabilities ───────────────────────────────────────
  const fetchAgentVulnerabilities = async (agentName) => {
    setModalLoading(true);
    setModalError(null);
    setExpandedVuln(null);
    setAgentVulnerabilities([]);
    setAgentStats(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/vulnerabilities/agent/${encodeURIComponent(agentName)}`,
        { headers: getAuthHeaders() }
      );

      if (response.status === 401) { handleAuthError(); return; }

      const data = await response.json();
      if (data.success) {
        setAgentVulnerabilities(data.vulnerabilities || []);
        setAgentStats(data.stats || null);
      } else {
        setModalError(data.error || "Failed to fetch vulnerabilities");
      }
    } catch {
      setModalError("Network error while fetching vulnerabilities");
    } finally {
      setModalLoading(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setFilters((f) => ({ ...f, page: newPage }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const clearFilters = () => {
    setFilters({ agent_name: "", os: "", min_vulns: "", page: 1, per_page: 20 });
  };

  const openAgentDetails = (agent) => {
    setSelectedAgent(agent);
    setModalOpen(true);
    fetchAgentVulnerabilities(agent.agent_name);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAgent(null);
    setAgentVulnerabilities([]);
    setAgentStats(null);
    setModalError(null);
    setExpandedVuln(null);
  };

  const toggleVulnDetails = (index) => {
    setExpandedVuln(expandedVuln === index ? null : index);
  };

  const getActiveFiltersCount = () =>
    [filters.agent_name, filters.os, filters.min_vulns].filter(Boolean).length;

  // ── Pagination renderer ────────────────────────────────────────────────────
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage   = Math.min(pagination.pages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    if (startPage > 1) {
      items.push(<PaginationItem key="first"><PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink></PaginationItem>);
      if (startPage > 2) items.push(<PaginationItem key="d1" disabled><PaginationLink>…</PaginationLink></PaginationItem>);
    }
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === pagination.page}>
          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }
    if (endPage < pagination.pages) {
      if (endPage < pagination.pages - 1) items.push(<PaginationItem key="d2" disabled><PaginationLink>…</PaginationLink></PaginationItem>);
      items.push(<PaginationItem key="last"><PaginationLink onClick={() => handlePageChange(pagination.pages)}>{pagination.pages}</PaginationLink></PaginationItem>);
    }
    return items;
  };

  // ── Derived modal stats ────────────────────────────────────────────────────
  const modalTotal    = agentStats ? parseInt(agentStats.total)    : agentVulnerabilities.length;
  const modalCritical = agentStats ? parseInt(agentStats.critical)  : agentVulnerabilities.filter(v => v.severity === "Critical").length;
  const modalHigh     = agentStats ? parseInt(agentStats.high)      : agentVulnerabilities.filter(v => v.severity === "High").length;
  const modalFixable  = agentStats ? parseInt(agentStats.fixable)   : agentVulnerabilities.filter(v => v.fix_available).length;
  const modalAvgCVSS  = agentStats?.avg_cvss ? parseFloat(agentStats.avg_cvss).toFixed(1) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Col xs={12}>
        <Card style={cardShell}>
          <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <span style={{ ...pageIconChip, marginRight: 8 }}>
                    <i className="ri-computer-line" style={{ color: "#FF5B00", fontSize: 18, lineHeight: 1 }} />
                  </span>
                  <h5 className="card-title mb-0">Agents Vulnerability Overview</h5>
                  {pagination.total > 0 && (
                    <span style={{ ...transparentBadge("#FF5B00"), borderRadius: "20px", marginLeft: "8px", fontSize: "11px", padding: "2px 10px" }}>
                      {pagination.total}
                    </span>
                  )}
                </div>
              </Col>
              <Col xs="auto">
                <div className="d-flex gap-2">
                  <Button
                    color={showFilters ? "primary" : "light"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <i className={showFilters ? "ri-filter-fill me-1" : "ri-filter-line me-1"} />
                    Filters
                    {getActiveFiltersCount() > 0 && (
                      <span style={{ ...transparentTag("#dc3545"), marginLeft: "6px", borderRadius: "20px", padding: "1px 7px" }}>
                        {getActiveFiltersCount()}
                      </span>
                    )}
                  </Button>
                  <Button color="light" size="sm" onClick={fetchAgents} disabled={loading}>
                    <i className="ri-refresh-line" />
                  </Button>
                </div>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="ri-error-warning-line me-2" />{error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            )}

            {/* Filters panel */}
            {showFilters && (
              <div className="border rounded p-3 mb-3 bg-light">
                <Row className="g-3">
                  <Col md={5}>
                    <Label className="form-label fw-semibold small">Agent Name</Label>
                    <Input
                      type="text"
                      placeholder="Search agent..."
                      value={filters.agent_name}
                      onChange={(e) => handleFilterChange("agent_name", e.target.value)}
                      bsSize="sm"
                    />
                  </Col>
                  <Col md={5}>
                    <Label className="form-label fw-semibold small">Min Vulnerabilities</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.min_vulns}
                      onChange={(e) => handleFilterChange("min_vulns", e.target.value)}
                      bsSize="sm"
                    />
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-semibold small opacity-0">Clear</Label>
                    <Button color="danger" size="sm" outline block onClick={clearFilters} id="clearFiltersBtn">
                      <i className="ri-close-line" /> Clear
                    </Button>
                    <UncontrolledTooltip target="clearFiltersBtn" placement="top">Clear all filters</UncontrolledTooltip>
                  </Col>
                </Row>
              </div>
            )}

            {/* Table body */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="text-muted mt-2 mb-0">Loading agents...</p>
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-5">
                <i className="ri-computer-line text-muted" style={{ fontSize: "64px" }} />
                <h5 className="mt-3 text-muted">No agents found</h5>
                <p className="text-muted mb-0">
                  {getActiveFiltersCount() > 0 ? "Try adjusting your filters" : "No agents with vulnerabilities"}
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table className="table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "30%" }}>Agent</th>
                        <th style={{ width: "12%" }} className="text-center">Total</th>
                        <th style={{ width: "12%" }} className="text-center">Critical</th>
                        <th style={{ width: "12%" }} className="text-center">High</th>
                        <th style={{ width: "12%" }} className="text-center">Avg CVSS</th>
                        <th style={{ width: "12%" }} className="text-center">Risk</th>
                        <th style={{ width: "10%" }} className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((agent, index) => {
                        const risk = getRiskLevel(agent.critical_count, agent.high_count);
                        return (
                          <tr key={agent.agent_id || index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-xs me-2">
                                  <span className="avatar-title bg-light text-primary rounded">
                                    <i className={getOSIcon(agent.agent_os)} />
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-semibold">{agent.agent_name}</div>
                                  {agent.agent_ip && (
                                    <small className="text-muted font-monospace">{agent.agent_ip}</small>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="text-center">
                              <span style={transparentBadge("#ffffff")}>
                                {parseInt(agent.total_vulnerabilities) || 0}
                              </span>
                            </td>

                            <td className="text-center">
                              {parseInt(agent.critical_count) > 0 ? (
                                <span style={transparentBadge(SEVERITY_COLORS.Critical)}>
                                  {agent.critical_count}
                                </span>
                              ) : <span className="text-muted">0</span>}
                            </td>

                            <td className="text-center">
                              {parseInt(agent.high_count) > 0 ? (
                                <span style={transparentBadge(SEVERITY_COLORS.High)}>
                                  {agent.high_count}
                                </span>
                              ) : <span className="text-muted">0</span>}
                            </td>

                            <td className="text-center">
                              {agent.avg_cvss ? (
                                <span style={getCVSSStyle(agent.avg_cvss)}>
                                  {parseFloat(agent.avg_cvss).toFixed(1)}
                                </span>
                              ) : <span className="text-muted">—</span>}
                            </td>

                            <td className="text-center">
                              <span style={{
                                ...transparentBadge(risk.color),
                                backgroundColor: `${risk.color}18`,
                                fontWeight: "700",
                                letterSpacing: "0.03em",
                              }}>
                                {risk.label}
                              </span>
                            </td>

                            <td className="text-center">
                              <button
                                onClick={() => openAgentDetails(agent)}
                                id={`viewBtn-${index}`}
                                style={{
                                  backgroundColor: "transparent",
                                  color: "#FF5B00",
                                  border: "1px solid #FF5B00",
                                  padding: "5px 14px",
                                  borderRadius: "6px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.backgroundColor = "#FF5B00";
                                  e.currentTarget.style.color = "#fff";
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#FF5B00";
                                }}
                              >
                                <i className="ri-eye-line" />
                                View
                              </button>
                              <UncontrolledTooltip target={`viewBtn-${index}`} placement="top">
                                View all vulnerabilities
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {pagination.pages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="text-muted">
                      Showing <strong>{(pagination.page - 1) * filters.per_page + 1}</strong> to{" "}
                      <strong>{Math.min(pagination.page * filters.per_page, pagination.total)}</strong> of{" "}
                      <strong>{pagination.total}</strong> agents
                    </div>
                    <Pagination className="mb-0">
                      <PaginationItem disabled={pagination.page === 1}>
                        <PaginationLink previous onClick={() => handlePageChange(pagination.page - 1)} />
                      </PaginationItem>
                      {renderPaginationItems()}
                      <PaginationItem disabled={pagination.page === pagination.pages}>
                        <PaginationLink next onClick={() => handlePageChange(pagination.page + 1)} />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* ── Agent Detail Modal ──────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="xl" scrollable>
        <ModalHeader toggle={closeModal}>
          <div className="d-flex align-items-center">
            <i className="ri-computer-line text-primary me-2 fs-5" />
            <div>
              <div className="fw-bold">{selectedAgent?.agent_name}</div>
              <small className="text-muted">
                {selectedAgent?.agent_os || "Unknown OS"}
                {selectedAgent?.agent_ip && ` · ${selectedAgent.agent_ip}`}
              </small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {modalLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2 mb-0">Loading vulnerabilities...</p>
            </div>
          ) : modalError ? (
            <div className="text-center py-5">
              <i className="ri-wifi-off-line text-muted" style={{ fontSize: "48px" }} />
              <p className="text-muted mt-3 mb-2">{modalError}</p>
              <Button size="sm" color="primary" outline onClick={() => fetchAgentVulnerabilities(selectedAgent?.agent_name)}>
                Retry
              </Button>
            </div>
          ) : agentVulnerabilities.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-shield-check-line text-primary" style={{ fontSize: "48px" }} />
              <p className="text-muted mt-3 mb-0">No vulnerabilities found for this agent</p>
            </div>
          ) : (
            <>
              {/* Stats summary row */}
              <Row className="mb-4">
                {[
                  { label: "Total",    value: modalTotal,                  color: "text-dark"    },
                  { label: "Critical", value: modalCritical,               color: "text-danger"  },
                  { label: "High",     value: modalHigh,                   color: "text-warning" },
                  { label: "Fixable",  value: modalFixable,                color: "text-primary" },
                  ...(modalAvgCVSS ? [{ label: "Avg CVSS", value: modalAvgCVSS, color: "text-info" }] : []),
                ].map(({ label, value, color }) => (
                  <Col key={label}>
                    <Card style={cardShell}>
                      <CardBody className="text-center py-3">
                        <p className="text-muted mb-1 small">{label}</p>
                        <h4 className={`mb-0 ${color}`}>{value}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Vulnerability rows */}
              <div className="table-responsive">
                <Table className="table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>CVE</th>
                      <th>Severity</th>
                      <th>CVSS</th>
                      <th>Package</th>
                      <th>Version</th>
                      <th>Detected</th>
                      <th className="text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentVulnerabilities.map((vuln, index) => {
                      const cvssFormatted = formatCVSS(vuln.cvss_score);
                      const isExpanded    = expandedVuln === index;
                      const refUrls       = parseReferenceUrls(vuln.reference_urls);

                      return (
                        <Fragment key={vuln.vulnerability_id || index}>
                          <tr>
                            <td>
                              <span className="font-monospace small fw-semibold">
                                {vuln.cve || "UNKNOWN"}
                              </span>
                            </td>
                            <td>
                              <span style={getSeverityStyle(vuln.severity)} className="d-inline-flex align-items-center gap-1">
                                <i className={getSeverityIcon(vuln.severity)} />
                                {vuln.severity}
                              </span>
                            </td>
                            <td>
                              {cvssFormatted
                                ? <span style={getCVSSStyle(vuln.cvss_score)}>{cvssFormatted}</span>
                                : <span className="text-muted">N/A</span>}
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: "200px" }}>
                                {vuln.package_name || "Unknown"}
                              </div>
                            </td>
                            <td>
                              <code className="small">{vuln.package_version || "N/A"}</code>
                            </td>
                            <td>
                              <small className="text-muted">{formatDate(vuln.detected_at)}</small>
                            </td>
                            <td className="text-center">
                              <Button color="light" size="sm" onClick={() => toggleVulnDetails(index)}>
                                <i className={isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                              </Button>
                            </td>
                          </tr>

                          {/* Expandable detail row */}
                          <tr>
                            <td colSpan="7" className="p-0 border-0">
                              <Collapse isOpen={isExpanded}>
                                <div className="bg-light p-3 border-top">
                                  <Row className="g-3">

                                    {/* Description */}
                                    {vuln.description && (
                                      <Col md={12}>
                                        <Card style={cardShell} className="mb-0">
                                          <CardBody>
                                            <h6 className="fw-semibold mb-2">
                                              <i className="ri-file-text-line me-2 text-primary" />Description
                                            </h6>
                                            <p className="text-muted mb-0 small">{vuln.description}</p>
                                          </CardBody>
                                        </Card>
                                      </Col>
                                    )}

                                    {/* Rule info */}
                                    {(vuln.rule_description || vuln.rule_id) && (
                                      <Col md={12}>
                                        <Card style={cardShell} className="mb-0">
                                          <CardBody>
                                            <h6 className="fw-semibold mb-2">
                                              <i className="ri-shield-line me-2 text-primary" />Rule Information
                                            </h6>
                                            <Row>
                                              {vuln.rule_id && (
                                                <Col md={3}>
                                                  <small className="text-muted">Rule ID</small>
                                                  <div><code>{vuln.rule_id}</code></div>
                                                </Col>
                                              )}
                                              {vuln.rule_level && (
                                                <Col md={3}>
                                                  <small className="text-muted">Rule Level</small>
                                                  <div><span style={transparentTag("#6c757d")}>{vuln.rule_level}</span></div>
                                                </Col>
                                              )}
                                              {vuln.rule_description && (
                                                <Col md={6}>
                                                  <small className="text-muted">Rule Description</small>
                                                  <div className="small">{vuln.rule_description}</div>
                                                </Col>
                                              )}
                                            </Row>
                                          </CardBody>
                                        </Card>
                                      </Col>
                                    )}

                                    {/* Package & Fix */}
                                    <Col md={6}>
                                      <Card style={cardShell} className="mb-0 h-100">
                                        <CardBody>
                                          <h6 className="fw-semibold mb-3">
                                            <i className="ri-archive-line me-2 text-primary" />Package & Fix
                                          </h6>
                                          <div className="mb-2">
                                            <small className="text-muted">Package</small>
                                            <div className="fw-medium">{vuln.package_name || "—"}</div>
                                          </div>
                                          {vuln.package_architecture && (
                                            <div className="mb-2">
                                              <small className="text-muted">Architecture</small>
                                              <div>{vuln.package_architecture}</div>
                                            </div>
                                          )}
                                          {vuln.package_condition && (
                                            <div className="mb-2">
                                              <small className="text-muted">Condition</small>
                                              <div className="small text-muted">{vuln.package_condition}</div>
                                            </div>
                                          )}
                                          <div className="mb-2">
                                            <small className="text-muted">Current Version</small>
                                            <div><code className="bg-light px-2 py-1 rounded">{vuln.package_version || "N/A"}</code></div>
                                          </div>
                                          {vuln.fixed_version && (
                                            <div className="mb-2">
                                              <small className="text-muted">Fixed Version</small>
                                              <div><code className="bg-primary-subtle text-primary px-2 py-1 rounded">{vuln.fixed_version}</code></div>
                                            </div>
                                          )}
                                          <div>
                                            <small className="text-muted">Fix Available</small>
                                            <div>
                                              {vuln.fix_available
                                                ? <span style={transparentTag(SEVERITY_COLORS.Low)}>Yes</span>
                                                : <span style={transparentTag(SEVERITY_COLORS.default)}>No</span>}
                                            </div>
                                          </div>
                                        </CardBody>
                                      </Card>
                                    </Col>

                                    {/* CVSS & Dates */}
                                    <Col md={6}>
                                      <Card style={cardShell} className="mb-0 h-100">
                                        <CardBody>
                                          <h6 className="fw-semibold mb-3">
                                            <i className="ri-bar-chart-line me-2 text-primary" />CVSS & Dates
                                          </h6>
                                          {vuln.cvss_version && (
                                            <div className="mb-2">
                                              <small className="text-muted">CVSS Version</small>
                                              <div>{vuln.cvss_version}</div>
                                            </div>
                                          )}
                                          {vuln.attack_vector && (
                                            <div className="mb-2">
                                              <small className="text-muted">Attack Vector</small>
                                              <div><span style={transparentTag(SEVERITY_COLORS.High)}>{vuln.attack_vector}</span></div>
                                            </div>
                                          )}
                                          {vuln.privileges_required && (
                                            <div className="mb-2">
                                              <small className="text-muted">Privileges Required</small>
                                              <div>{vuln.privileges_required}</div>
                                            </div>
                                          )}
                                          {vuln.user_interaction && (
                                            <div className="mb-2">
                                              <small className="text-muted">User Interaction</small>
                                              <div>{vuln.user_interaction}</div>
                                            </div>
                                          )}
                                          {vuln.cwe_id && (
                                            <div className="mb-2">
                                              <small className="text-muted">CWE</small>
                                              <div><code>{vuln.cwe_id}</code></div>
                                            </div>
                                          )}
                                          {vuln.published_date && (
                                            <div className="mb-2">
                                              <small className="text-muted">Published</small>
                                              <div><i className="ri-calendar-line me-1" />{formatDate(vuln.published_date)}</div>
                                            </div>
                                          )}
                                          {vuln.updated_date && (
                                            <div className="mb-2">
                                              <small className="text-muted">Last Updated</small>
                                              <div><i className="ri-calendar-check-line me-1" />{formatDate(vuln.updated_date)}</div>
                                            </div>
                                          )}
                                          {vuln.priority && (
                                            <div>
                                              <small className="text-muted">Priority</small>
                                              <div>
                                                <span style={transparentTag(
                                                  SEVERITY_COLORS[
                                                    vuln.priority.charAt(0).toUpperCase() +
                                                    vuln.priority.slice(1).toLowerCase()
                                                  ] || SEVERITY_COLORS.default
                                                )}>
                                                  {vuln.priority}
                                                </span>
                                              </div>
                                            </div>
                                          )}
                                        </CardBody>
                                      </Card>
                                    </Col>

                                    {/* Reference URLs */}
                                    {refUrls.length > 0 && (
                                      <Col md={12}>
                                        <Card style={cardShell} className="mb-0">
                                          <CardBody>
                                            <h6 className="fw-semibold mb-2">
                                              <i className="ri-link me-2 text-danger" />References
                                            </h6>
                                            <div className="d-flex flex-column gap-2">
                                              {refUrls.map((url, idx) => (
                                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                                                   className="text-decoration-none small">
                                                  <i className="ri-external-link-line me-1" />{url}
                                                </a>
                                              ))}
                                            </div>
                                          </CardBody>
                                        </Card>
                                      </Col>
                                    )}

                                  </Row>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          <div className="d-flex justify-content-end mt-4">
            <Button color="primary" onClick={closeModal}>
              <i className="ri-close-line me-1" /> Close
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default EnhancedVulnerabilityTable;
